// The deal side of an event settlement (ADR 0039): what BRE and one
// counterparty (a promoter, an artist, a renter) owe each other after a
// night, from the settlement's frozen lines plus the deal's terms.
//
// Hub owns settlement (owner, 2026-10-14). This is still a management
// statement, not accounting: nothing here is an invoice, a bill, a payment
// or a journal entry, Hub moves no money and posts nothing to QuickBooks
// (rule 3, ADR 0003). BRE doesn't charge sales tax (owner, 2026-10-14), so
// a statement has no tax line.
//
// Everything is pure: the same lines and terms always give the same
// statement, every step says how it was worked out, and a figure the deal
// needs that no line supplies makes the statement INCOMPLETE (amount due
// null), never zero (rule 11).

import { settlementTotals, type SettlementCategory, type SettlementSourcePull, type SettlementTotals } from './event-settlement'

// ------------------------------------------------------------------ rounding

// THE ROUNDING RULE. A percentage of an amount is worked out exactly in
// integer arithmetic and rounded once to the nearest cent, halves away from
// zero (BTownTix's own ledger rule, packages/domain/src/money.ts). Each
// percentage step rounds on its own, then steps add: there is no rounding
// of a sum. Basis points: 10000 = 100%.
export const ROUNDING_RULE_TEXT = 'Each percentage is rounded once to the nearest cent, halves away from zero; rounded steps are then added.'

export function percentOf(cents: number, bps: number): number {
  if (!Number.isSafeInteger(cents) || !Number.isSafeInteger(bps)) throw new Error('Whole cents and whole basis points only')
  const product = BigInt(cents) * BigInt(bps)
  const negative = product < 0n
  const abs = negative ? -product : product
  const q = abs / 10000n
  const r = abs % 10000n
  const rounded = r * 2n >= 10000n ? q + 1n : q
  return Number(negative ? -rounded : rounded)
}

// ------------------------------------------------------------------- terms

// 'talent':     BRE presents the show and keeps the door; the counterparty
//               (an artist or a promoter) is paid the deal amount.
// 'rental':     the counterparty rents the venue and owns the door; the deal
//               amount is BRE's rent.
// 'co_promote': BRE and a co-promoter split the show's net, profit or loss,
//               after show expenses and the acts' pay (ADR 0040).
export type DealKind = 'talent' | 'rental' | 'co_promote'

// How the deal amount is worked out from the basis:
//   flat          the flat amount
//   percentage    percent of the basis
//   versus        the greater of the flat amount and percent of the basis
//   plus          the flat amount plus percent of whatever the basis makes
//                 above the split point (Booking's "versus" deal)
//   walkout       the guarantee plus percent of whatever the basis makes
//                 above a split point worked out from the guarantee, the show
//                 expenses and BRE's profit percentage (ADR 0040)
//   profit_split  a co-promote: percent of the show's net (ADR 0040)
// For flat-and-percent deals a cap, when set, limits the deal amount.
export type DealFormula = 'flat' | 'percentage' | 'versus' | 'plus' | 'walkout' | 'profit_split'

// What the percentage is of:
//   gross               the door: ticket sales after discounts and refunds,
//                       before buyer fees (fees are never in the door)
//   net                 the door less BRE's per-ticket fee
//   net_after_expenses  net less the expenses marked "comes off before the split"
export type DealBasis = 'gross' | 'net' | 'net_after_expenses'

// What an expense line means for the deal:
//   bre           BRE's own cost; the deal doesn't see it (the default)
//   before_split  a show expense: comes off the net before the percentage
//   charged       billed to the counterparty in full
export type DealTreatment = 'bre' | 'before_split' | 'charged'

export type DealAdvance = {
  // to_counterparty: BRE already paid them (an artist deposit, a night-of
  // cash payout). from_counterparty: they already paid BRE (a renter deposit).
  direction: 'to_counterparty' | 'from_counterparty'
  label: string
  amountCents: number
  paidOn: string | null
  reference: string | null
}

export type DealAdjustment = {
  // Positive: BRE owes the counterparty more. Negative: they owe BRE.
  label: string
  amountCents: number
  note: string | null
}

// Where an act sits on the bill (ADR 0040). Only words on the statement:
// the order of the parties is what the math uses.
export type DealBilling = 'headliner' | 'co_headliner' | 'support' | 'opener'

// A bonus added to the artist's share (ADR 0040) once a measure reaches a
// threshold: paid tickets (a count) or the door gross (cents). "Reached"
// means at or above the threshold.
export type DealBonus = {
  measure: 'paid_tickets' | 'door_gross'
  threshold: number
  amountCents: number
  label: string | null
}

// Card processing charged to the counterparty as its own step (ADR 0040).
//   share 'percent'   that percentage of the processing cost (100% = all of it)
//   share 'pro_rata'  their share of the door: their pay over the door gross
//                     (a talent deal), or their share of the net (a co-promote)
//   include 'ticketing'  BTownTix's Stripe processing cost for the event
//   include 'all'        that plus every other card processing line (POS's,
//                        or one staff entered)
export type DealProcessing = { share: 'percent' | 'pro_rata'; percentBps: number | null; include: 'ticketing' | 'all' }

export type SettlementDealTerms = {
  counterpartyName: string
  counterpartyRole: 'promoter' | 'artist' | 'renter' | 'co_promoter'
  organizationId: string | null
  kind: DealKind
  formula: DealFormula
  flatCents: number | null
  percentBps: number | null
  basis: DealBasis
  splitPointCents: number | null
  capCents: number | null
  // Who holds the ticket money: 'bre' when it sold through BTownTix.
  doorCollectedBy: 'bre' | 'counterparty'
  // BRE's fee per paid ticket, taken out of the door before the basis.
  perTicketFeeCents: number | null
  // The counterparty's share of bar and food sales (after discounts, comps
  // and refunds; no tips).
  barSplitBps: number | null
  // 'venue': BRE sold the counterparty's merch (the merch lines are their
  // money) and keeps a commission. 'counterparty': they sold it and owe BRE
  // the commission on what they report. 'none': no merch deal.
  merchSoldBy: 'venue' | 'counterparty' | 'none'
  merchCommissionBps: number | null
  advances: DealAdvance[]
  adjustments: DealAdjustment[]
  // Where the terms came from: typed in Hub, or copied from Booking's offer
  // or contract (and then editable in Hub; Booking is never written).
  origin: { kind: 'hub' } | { kind: 'booking'; sourceId: string; entityType: string; source: 'contract' | 'offer'; status: string; dealType: string | null; copiedAt: string }
  note: string | null

  // ADR 0040. Every one of these is optional and absent unless set, so the
  // terms of a deal made before them (and the hash of its approval) are
  // exactly as they were.
  billing?: DealBilling | null
  // The part of the door this act's percentage is of. Absent: the whole
  // door. The door is only divided among acts when a deal says so.
  doorShareBps?: number | null
  // The part of the shared show expenses that comes off this act's split.
  // Absent: all of them (they come off every split that uses them, once).
  expenseShareBps?: number | null
  // The pay of the acts billed below this one comes off this act's split as
  // a show expense.
  supportAsExpense?: boolean
  // A walkout deal: BRE's profit, as a percentage of guarantee + expenses.
  promoterProfitBps?: number | null
  // A co-promote: the co-promoter's share of a loss. Absent: the same as
  // their share of a profit (percentBps).
  lossShareBps?: number | null
  bonuses?: DealBonus[]
  // 'stack': every bonus reached pays. 'highest': only the largest reached.
  bonusMode?: 'stack' | 'highest'
  processing?: DealProcessing | null

  // ADR 0046. A ticketed rental settled as Booking's facility-fee waterfall
  // works: off the door come the facility fees, the ticketing fees, then
  // ASCAP/BMI and card fees as percentages of what is left; the expenses
  // charged to the renter come off before BRE's share too. Absent unless
  // set, so every deal made before it hashes as it did. Rental deals only.
  waterfall?: RentalWaterfall | null
}

// Booking's rental settlement waterfall (its rentals/[id]/settlement screen),
// as terms of Hub's rental deal (ADR 0046):
//   door                                  (BTownTix's door, the renter's money)
//   less facility fees                    facilityFeesCents, or the per-ticket fee × paid tickets
//   less ticketing fees                   ticketingFeesCents
//   = door after fees
//   less ASCAP/BMI                        ascapBps of the door after fees
//   less card fees                        cardFeeBps of the door after fees
//   less expenses charged to the renter
//   = the net BRE's share is worked out on (basis net_after_expenses)
// BRE keeps the fees, the ASCAP/BMI and card amounts and the rent; the rest
// is the renter's, less what was already paid to them (early payouts).
export type RentalWaterfall = {
  // Facility fees inside the door (Booking's per-tier facility fee × tickets
  // sold), in cents. Null: not known yet, so the statement is incomplete.
  // Zero when the per-ticket fee carries them, or when BTownTix charged them
  // as a separate buyer fee (then they are not in the door at all).
  facilityFeesCents: number | null
  ticketingFeesCents: number
  ascapBps: number
  cardFeeBps: number
}

// One counterparty in a settlement (ADR 0040): a settlement's deal can have
// several (a headliner, support, an opener, a co-promoter), each with its own
// terms and its own statement. The first by position is the lead.
export type SettlementParty = { key: string; position: number; terms: SettlementDealTerms }
// The key of a settlement's first party (every deal made before ADR 0040).
export const LEAD_PARTY_KEY = 'main'

export const DEAL_KIND_LABELS: Record<DealKind, string> = {
  talent: 'Artist or promoter is paid (BRE keeps the door)',
  rental: 'Renter pays BRE (the renter keeps the door)',
  co_promote: 'Co-promoter splits the show’s net with BRE (profit or loss)',
}
export const DEAL_FORMULA_LABELS: Record<DealFormula, string> = {
  flat: 'Flat amount',
  percentage: 'Percentage',
  versus: 'Versus: greater of the flat amount or the percentage',
  plus: 'Flat amount plus a percentage above a split point',
  walkout: 'Guarantee plus a percentage above the walkout (guarantee + expenses + BRE’s profit)',
  profit_split: 'Split of the show’s net',
}
// The formulas each kind of deal can use.
export const DEAL_FORMULAS_BY_KIND: Record<DealKind, DealFormula[]> = {
  talent: ['flat', 'percentage', 'versus', 'plus', 'walkout'],
  rental: ['flat', 'percentage', 'versus', 'plus'],
  co_promote: ['profit_split'],
}
export const DEAL_BASIS_LABELS: Record<DealBasis, string> = { gross: 'Door gross', net: 'Door net of per-ticket fees', net_after_expenses: 'Door net, after show expenses' }
export const DEAL_TREATMENT_LABELS: Record<DealTreatment, string> = { bre: 'BRE’s cost (not in the deal)', before_split: 'Show expense: comes off before the split', charged: 'Charged to the counterparty' }
export const COUNTERPARTY_ROLE_LABELS: Record<SettlementDealTerms['counterpartyRole'], string> = { promoter: 'Promoter', artist: 'Artist', renter: 'Renter', co_promoter: 'Co-promoter' }
export const DEAL_BILLING_LABELS: Record<DealBilling, string> = { headliner: 'Headliner', co_headliner: 'Co-headliner', support: 'Support', opener: 'Opener' }

// ------------------------------------------------------- Booking's deal types

// Booking's offers (apps/web/src/lib/utils/settlement.ts) in Hub's words.
// Booking's net is gross less ticketing fees; Hub's door already excludes
// buyer fees, so Booking's net reads as Hub's 'net'.
export function formulaFromBooking(dealType: string | null): DealFormula | null {
  switch (dealType) {
    case 'flat':
      return 'flat'
    case 'guarantee_vs_door':
      return 'versus'
    case 'ticket_split':
    case 'percentage_only':
      return 'percentage'
    case 'versus':
      // Booking's "versus" is guarantee + door % above a floor, capped.
      return 'plus'
    default:
      return null
  }
}

// --------------------------------------------------------------- the inputs

export type BaseKey = 'door' | 'paidTickets' | 'bar' | 'merch' | 'feesForOthers' | 'processing'

export type DealBase = {
  key: BaseKey
  label: string
  // null: missing. Never treated as zero.
  value: number | null
  explanation: string
  missingReason: string | null
}

type LineLike = {
  category: SettlementCategory | string
  amountCents: number
  dealTreatment?: DealTreatment
  label: string
  id?: string
  // ADR 0040: the party an expense belongs to. Absent: shared (a show
  // expense) or the lead's (a charged expense).
  dealPartyKey?: string | null
  source?: { sourceService: string; figureKey?: string } | null
}

const sum = (lines: LineLike[], categories: string[]) => lines.filter((l) => categories.includes(l.category)).reduce((acc, l) => acc + l.amountCents, 0)

// What each base is made of, by category.
export const DOOR_CATEGORIES = ['ticket_revenue']
export const BAR_CATEGORIES = ['fnb_merch']
export const BAR_DEDUCTION_CATEGORIES = ['fnb_refunds']
export const MERCH_CATEGORIES = ['merch']
export const FEES_FOR_OTHERS_CATEGORIES = ['fees_for_others']
export const PROCESSING_CATEGORIES = ['processing']

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`

// The bases a deal can use, from the frozen lines and what each app said.
export function dealBases(lines: LineLike[], sources: SettlementSourcePull[]): Record<BaseKey, DealBase> {
  const by = (service: string) => sources.filter((s) => s.sourceService === service)
  const down = (service: string) => by(service).filter((s) => s.status !== 'ok')
  const name = (service: string) => (service === 'bre-ticketing' ? 'BTownTix' : service === 'pos' ? 'POS' : 'Booking')
  const downReason = (service: string) => {
    const d = down(service)
    return d.length ? `${name(service)} didn’t answer for ${d.map((s) => s.title).join(', ')} (${d[0]!.reason ?? d[0]!.status}).` : null
  }

  const doorLines = lines.filter((l) => DOOR_CATEGORIES.includes(l.category))
  const door: DealBase = {
    key: 'door',
    label: 'Door gross',
    value: sum(lines, DOOR_CATEGORIES),
    explanation: `Ticket sales after discounts and refunds, before buyer fees: ${doorLines.length} line${doorLines.length === 1 ? '' : 's'} in Ticket revenue.`,
    missingReason: null,
  }
  door.missingReason = downReason('bre-ticketing') ?? (doorLines.length === 0 ? 'No ticket revenue line. Link the BTownTix event, or add a Ticket revenue line (a line of 0.00 if the door really took nothing).' : null)

  const tix = by('bre-ticketing').filter((s) => s.status === 'ok')
  const counted = tix.filter((s) => s.counts)
  const paid: DealBase = {
    key: 'paidTickets',
    label: 'Paid tickets',
    value: counted.reduce((acc, s) => acc + (s.counts?.paidTickets ?? 0), 0),
    explanation: 'Paid tickets still active (comps excluded), as BTownTix counted them.',
    missingReason: null,
  }
  paid.missingReason = downReason('bre-ticketing') ?? (tix.length === 0 ? 'No BTownTix event is linked, so nobody counted paid tickets.' : counted.length < tix.length ? 'BTownTix didn’t send its settlement figures, so paid tickets weren’t counted.' : null)

  const barLines = lines.filter((l) => BAR_CATEGORIES.includes(l.category))
  const posNoMoney = by('pos').filter((s) => s.status === 'ok' && !s.figures.some((f) => f.category === 'fnb_merch'))
  const bar: DealBase = {
    key: 'bar',
    label: 'Bar and food sales',
    value: sum(lines, BAR_CATEGORIES) - sum(lines, BAR_DEDUCTION_CATEGORIES),
    explanation: 'Food & beverage sales after discounts and comps, less refunds of goods. No tips, no card surcharge.',
    missingReason: null,
  }
  bar.missingReason =
    downReason('pos') ??
    (posNoMoney.length ? `POS answered for ${posNoMoney.map((s) => s.title).join(', ')} without its sales split (only a charged total, which includes tips).` : null) ??
    (barLines.length === 0 ? 'No food & beverage line. Link the POS event, or add the bar sales as a line.' : null)

  const merchLines = lines.filter((l) => MERCH_CATEGORIES.includes(l.category))
  const merch: DealBase = {
    key: 'merch',
    label: 'Merch sales',
    value: sum(lines, MERCH_CATEGORIES),
    explanation: `${merchLines.length} line${merchLines.length === 1 ? '' : 's'} in Merch sales.`,
    missingReason: merchLines.length === 0 ? 'No Merch sales line. Add the merch gross (a line of 0.00 if nothing sold).' : null,
  }

  const fees = lines.filter((l) => FEES_FOR_OTHERS_CATEGORIES.includes(l.category))
  const feeless = tix.filter((s) => s.feeOwnership !== true)
  const feesForOthers: DealBase = {
    key: 'feesForOthers',
    label: 'Buyer fees held for others',
    value: sum(lines, FEES_FOR_OTHERS_CATEGORIES),
    explanation: `Buyer fees BTownTix recorded as someone else’s (a promoter fee): ${fees.length} line${fees.length === 1 ? '' : 's'}.`,
    missingReason: downReason('bre-ticketing') ?? (feeless.length ? 'BTownTix didn’t say who keeps each fee for this event.' : null),
  }

  // BTownTix's Stripe processing cost for the event, from its settlement
  // figures (ADR 0039). Without them it isn't known: missing, never zero.
  const tixProcessing = lines.filter((l) => PROCESSING_CATEGORIES.includes(l.category) && l.source?.sourceService === 'bre-ticketing')
  const processing: DealBase = {
    key: 'processing',
    label: 'Card processing (BTownTix)',
    value: tixProcessing.reduce((acc, l) => acc + l.amountCents, 0),
    explanation: 'Stripe’s fees on this event’s BTownTix payments, from BTownTix’s ledger.',
    missingReason:
      downReason('bre-ticketing') ??
      (tix.length === 0 ? 'No BTownTix event is linked, so nobody reported the card processing cost.' : feeless.length ? 'BTownTix didn’t send its settlement figures, so its card processing cost isn’t known.' : null),
  }
  return { door, paidTickets: paid, bar, merch, feesForOthers, processing }
}

// ------------------------------------------------------------ the statement

export type StatementStep = {
  key: string
  label: string
  // How it was worked out, in words and numbers.
  explanation: string
  // Toward the counterparty: positive means BRE owes them this, negative
  // means it comes off what BRE owes (or adds to what they owe BRE).
  // null when an input is missing.
  amountCents: number | null
  // 'info' steps show a working figure (the basis, the deal amount before a
  // cap) and are in no total.
  role: 'due' | 'info'
}

export type StatementMissing = { base: BaseKey | 'acts' | 'facilityFees'; label: string; reason: string }

export type DealStatement = {
  complete: boolean
  missing: StatementMissing[]
  warnings: string[]
  bases: Record<BaseKey, DealBase>
  basisCents: number | null
  dealAmountCents: number | null
  steps: StatementStep[]
  // Sum of the 'due' steps. Positive: BRE owes the counterparty. Negative:
  // the counterparty owes BRE. Null while incomplete.
  amountDueCents: number | null
  roundingRule: string
  // ADR 0040. What the deal pays them before anything else on the statement:
  // a talent deal's amount plus its bonuses, a co-promoter's share of the
  // net, a renter's rent. Null while it can't be worked out.
  payoutCents?: number | null
  // The door gross at which the percentage starts paying more than the
  // guarantee (or above the split point), with tonight's paid tickets and
  // expenses held as they were. Null when the deal has none.
  walkoutDoorCents?: number | null
}

export const usd = (cents: number): string => {
  const sign = cents < 0 ? '-' : ''
  const abs = Math.abs(cents)
  const dollars = Math.floor(abs / 100).toLocaleString('en-US')
  return `${sign}$${dollars}.${String(abs % 100).padStart(2, '0')}`
}
export const pct = (bps: number): string => `${(bps / 100).toFixed(2).replace(/\.?0+$/, '')}%`

// a × num ÷ den, rounded once to the nearest cent, halves away from zero
// (the rounding rule, for a share that isn't a whole percentage).
export function ratioOf(cents: number, num: number, den: number): number {
  if (den === 0) throw new Error('A ratio needs a denominator')
  const product = BigInt(cents) * BigInt(num)
  const d = BigInt(den)
  const negative = product < 0n !== d < 0n
  const p = product < 0n ? -product : product
  const dd = d < 0n ? -d : d
  const q = p / dd
  const rounded = (p % dd) * 2n >= dd ? q + 1n : q
  return Number(negative ? -rounded : rounded)
}

// The smallest whole amount x with percentOf(x, bps) >= target: how much
// basis it takes for a percentage to reach an amount, under the rounding
// rule. Null when the percentage is zero (it never does).
export function smallestReaching(target: number, bps: number): number | null {
  if (bps <= 0) return target <= 0 ? 0 : null
  if (target <= 0) return 0
  // percentOf(x) >= T  <=>  x * bps + 5000 >= T * 10000  <=>  x >= (2T - 1) * 5000 / bps
  const n = (2n * BigInt(target) - 1n) * 5000n
  const b = BigInt(bps)
  return Number((n + b - 1n) / b)
}

type Expense = { label: string; amountCents: number; treatment: DealTreatment; category: string; partyKey: string | null }

const COST_CATEGORIES_FOR_DEAL = new Set(['talent', 'production', 'staffing', 'security', 'supplies', 'marketing', 'other_cost', 'processing'])

// What one party's statement needs from the others (ADR 0040).
export type DealContext = {
  partyKey: string
  // The lead takes the charged expenses nobody else is named on, and the
  // buyer fees held for others.
  isLead: boolean
  // The acts billed below this one, with their pay (null while unknown).
  lower: { name: string; payoutCents: number | null }[]
  // Every act in the deal, with their pay: a co-promote's talent cost.
  talent: { name: string; payoutCents: number | null }[]
  // How many parties the settlement's deal has.
  partyCount: number
}
const SOLO: DealContext = { partyKey: LEAD_PARTY_KEY, isLead: true, lower: [], talent: [], partyCount: 1 }

export function computeDealStatement(terms: SettlementDealTerms, lines: LineLike[], sources: SettlementSourcePull[], ctx: DealContext = SOLO): DealStatement {
  const bases = dealBases(lines, sources)
  const missing: StatementMissing[] = []
  const warnings: string[] = []
  const need = (key: BaseKey): number | null => {
    const base = bases[key]
    if (base.missingReason !== null) {
      if (!missing.some((m) => m.base === key)) missing.push({ base: key, label: base.label, reason: base.missingReason })
      return null
    }
    return base.value
  }
  const steps: StatementStep[] = []
  const due = (key: string, label: string, explanation: string, amountCents: number | null) => steps.push({ key, label, explanation, amountCents, role: 'due' })
  const info = (key: string, label: string, explanation: string, amountCents: number | null) => steps.push({ key, label, explanation, amountCents, role: 'info' })

  const expenses: Expense[] = lines
    .filter((l) => COST_CATEGORIES_FOR_DEAL.has(l.category) && (l.dealTreatment ?? 'bre') !== 'bre')
    .map((l) => ({ label: l.label, amountCents: l.amountCents, treatment: l.dealTreatment!, category: l.category, partyKey: l.dealPartyKey ?? null }))
  // Show expenses: shared ones (no party named) come off every split that
  // uses them, once each; one named on a party comes off only theirs.
  const sharedBefore = expenses.filter((e) => e.treatment === 'before_split' && e.partyKey === null)
  const ownBefore = expenses.filter((e) => e.treatment === 'before_split' && e.partyKey === ctx.partyKey)
  const allBefore = expenses.filter((e) => e.treatment === 'before_split')
  const charged = expenses.filter((e) => e.treatment === 'charged' && (e.partyKey === ctx.partyKey || (e.partyKey === null && ctx.isLead)))
  const total = (xs: Expense[]) => xs.reduce((a, e) => a + e.amountCents, 0)
  const sharedTotal = total(sharedBefore)
  const sharedPart = terms.expenseShareBps == null ? sharedTotal : percentOf(sharedTotal, terms.expenseShareBps)
  const ownTotal = total(ownBefore)
  const beforeSplit = [...sharedBefore, ...ownBefore]
  const beforeSplitTotal = sharedPart + ownTotal
  const expenseWords = () => {
    const parts: string[] = []
    if (terms.expenseShareBps == null) parts.push(`${usd(sharedTotal)} (${plural(sharedBefore.length, 'line')}${ownBefore.length ? ' shared' : ''})`)
    else parts.push(`${pct(terms.expenseShareBps)} of ${usd(sharedTotal)} shared (${plural(sharedBefore.length, 'line')}) = ${usd(sharedPart)}`)
    if (ownBefore.length) parts.push(`${usd(ownTotal)} of their own (${plural(ownBefore.length, 'line')})`)
    return parts.join(' plus ')
  }

  // The pay of the acts billed below this one, as a show expense (ADR 0040).
  const usesSupport = terms.kind === 'talent' && terms.supportAsExpense === true && ctx.lower.length > 0 && (terms.basis === 'net_after_expenses' || terms.formula === 'walkout')
  let supportCost: number | null = 0
  if (usesSupport) {
    const unknown = ctx.lower.filter((a) => a.payoutCents === null)
    if (unknown.length) {
      supportCost = null
      if (!missing.some((m) => m.base === 'acts')) missing.push({ base: 'acts', label: 'Other acts’ pay', reason: `The pay of ${unknown.map((a) => a.name).join(', ')} can’t be worked out yet, and it comes off this split.` })
    } else supportCost = ctx.lower.reduce((a, x) => a + x.payoutCents!, 0)
  }
  const supportWords = () => `${usd(supportCost ?? 0)} paid to ${ctx.lower.map((a) => a.name).join(', ')}`

  const isCopro = terms.kind === 'co_promote'
  const usesBasis = terms.formula !== 'flat' && !isCopro
  const bps = terms.percentBps ?? 0
  const flat = terms.flatCents ?? 0
  const share = terms.doorShareBps ?? null
  const walkoutFormula = terms.formula === 'walkout'
  // A walkout's split point already holds the expenses; its basis never subtracts them again.
  const expensesInBasis = terms.basis === 'net_after_expenses' && !walkoutFormula

  // The door, the per-ticket fee and the basis.
  const needsDoor = usesBasis || isCopro || (terms.kind === 'talent' ? terms.doorCollectedBy === 'counterparty' : terms.kind === 'rental' ? terms.doorCollectedBy === 'bre' : false) || (terms.bonuses ?? []).some((b) => b.measure === 'door_gross')
  const door = needsDoor ? need('door') : bases.door.missingReason === null ? bases.door.value : null
  // The per-ticket fee matters to a rental always, and to the others only through a net basis.
  const perTicketMatters = terms.kind === 'rental' || ((usesBasis || isCopro) && terms.basis !== 'gross')
  let perTicketTotal: number | null = 0
  if (terms.perTicketFeeCents && perTicketMatters) {
    const count = need('paidTickets')
    perTicketTotal = count === null ? null : count * terms.perTicketFeeCents
  }
  // ADR 0046: Booking's facility-fee waterfall on a rental (see RentalWaterfall).
  const rw =
    terms.kind === 'rental' && terms.waterfall
      ? (() => {
          const w = terms.waterfall!
          const facility = w.facilityFeesCents
          if (facility === null && !missing.some((m) => m.base === 'facilityFees'))
            missing.push({ base: 'facilityFees', label: 'Facility fees in the door', reason: 'Enter the facility fees inside the door (per-tier facility fees × tickets sold), or 0 when BTownTix charged them as a separate fee.' })
          const afterFees = door === null || perTicketTotal === null || facility === null ? null : door - perTicketTotal - facility - w.ticketingFeesCents
          const ascap = afterFees === null ? null : percentOf(afterFees, w.ascapBps)
          const card = afterFees === null ? null : percentOf(afterFees, w.cardFeeBps)
          const chargedTotal = total(charged)
          return { facility, ticketing: w.ticketingFeesCents, afterFees, ascap, card, chargedTotal }
        })()
      : null
  let basisCents: number | null = null
  let allocated: number | null = null
  if (usesBasis && rw) {
    // The waterfall's net: the door after fees, ASCAP/BMI and card fees, and
    // (net after expenses) the expenses charged to the renter and the show expenses.
    if (rw.afterFees !== null && rw.ascap !== null && rw.card !== null) {
      const net = rw.afterFees - rw.ascap - rw.card
      basisCents = terms.basis === 'gross' ? door : terms.basis === 'net' ? net : net - rw.chargedTotal - beforeSplitTotal
    }
    const parts = [`door ${door === null ? 'missing' : usd(door)}`]
    if (terms.basis !== 'gross') {
      if (perTicketTotal) parts.push(`less facility fees per ticket ${usd(perTicketTotal)}`)
      parts.push(`less facility fees ${rw.facility === null ? 'missing' : usd(rw.facility)}`)
      if (rw.ticketing) parts.push(`less ticketing fees ${usd(rw.ticketing)}`)
      parts.push(`less ASCAP/BMI ${rw.ascap === null ? 'missing' : usd(rw.ascap)} and card fees ${rw.card === null ? 'missing' : usd(rw.card)}`)
      if (terms.basis === 'net_after_expenses') parts.push(`less expenses charged to the renter ${usd(rw.chargedTotal)}${beforeSplitTotal ? ` and show expenses ${usd(beforeSplitTotal)}` : ''}`)
    }
    info('basis', `Basis: ${terms.basis === 'gross' ? DEAL_BASIS_LABELS.gross : terms.basis === 'net' ? 'Door net of fees, ASCAP/BMI and card fees' : 'Door net of fees, ASCAP/BMI, card fees and expenses'}`, parts.join(', '), basisCents)
  } else if (usesBasis) {
    if (door !== null && perTicketTotal !== null) {
      const pre = terms.basis === 'gross' ? door : door - perTicketTotal
      allocated = share === null ? pre : percentOf(pre, share)
      if (!expensesInBasis) basisCents = allocated
      else if (supportCost !== null) basisCents = allocated - beforeSplitTotal - supportCost
    }
    const parts = [`door ${door === null ? 'missing' : usd(door)}`]
    if (terms.basis !== 'gross') parts.push(`less per-ticket fees ${perTicketTotal === null ? 'missing' : usd(perTicketTotal)}`)
    if (share !== null) parts.push(`× ${pct(share)} of the door allocated to this act${allocated === null ? '' : ` (${usd(allocated)})`}`)
    if (expensesInBasis) {
      if (share === null && ownBefore.length === 0 && terms.expenseShareBps == null) parts.push(`less show expenses ${usd(beforeSplitTotal)} (${plural(beforeSplit.length, 'line')})`)
      else parts.push(`less show expenses ${expenseWords()}`)
      if (usesSupport) parts.push(`less the other acts’ pay ${supportCost === null ? 'missing' : supportWords()}`)
    }
    info('basis', `Basis: ${DEAL_BASIS_LABELS[terms.basis]}`, parts.join(', '), basisCents)
  }
  if (beforeSplit.length && ctx.partyCount === 1 && !isCopro && !expensesInBasis && !walkoutFormula && !rw)
    warnings.push(`${beforeSplit.length} expense${beforeSplit.length === 1 ? ' is' : 's are'} marked “comes off before the split”, but this deal’s percentage is of ${DEAL_BASIS_LABELS[terms.basis].toLowerCase()}, so they change nothing.`)

  // A walkout deal's split point: guarantee + show expenses (+ the other
  // acts' pay) + BRE's profit percentage of that.
  let walkoutSplit: number | null = null
  if (walkoutFormula) {
    const profitBps = terms.promoterProfitBps ?? 0
    if (supportCost !== null) {
      const costs = flat + beforeSplitTotal + supportCost
      const profit = percentOf(costs, profitBps)
      walkoutSplit = costs + profit
      const what = [`guarantee ${usd(flat)}`, `show expenses ${expenseWords()}`]
      if (usesSupport) what.push(`the other acts’ pay ${supportWords()}`)
      info('walkout_split', 'Split point (the walkout)', `${what.join(' + ')} = ${usd(costs)}, plus BRE’s profit ${pct(profitBps)} of that (${usd(profit)}) = ${usd(walkoutSplit)}`, walkoutSplit)
    } else info('walkout_split', 'Split point (the walkout)', 'needs the other acts’ pay', null)
  }

  // The deal amount.
  let dealAmount: number | null = null
  let dealWork = ''
  if (terms.formula === 'flat') {
    dealAmount = flat
    dealWork = `flat ${usd(flat)}`
  } else if (basisCents !== null && !isCopro) {
    const shareOfBasis = percentOf(basisCents, bps)
    if (terms.formula === 'percentage') {
      dealAmount = shareOfBasis
      dealWork = `${pct(bps)} of ${usd(basisCents)} = ${usd(shareOfBasis)}`
    } else if (terms.formula === 'versus') {
      dealAmount = Math.max(flat, shareOfBasis)
      dealWork = `greater of flat ${usd(flat)} and ${pct(bps)} of ${usd(basisCents)} (${usd(shareOfBasis)}) = ${usd(dealAmount)}`
    } else if (terms.formula === 'walkout') {
      if (walkoutSplit !== null) {
        const above = Math.max(0, basisCents - walkoutSplit)
        const extra = percentOf(above, bps)
        dealAmount = flat + extra
        dealWork = `guarantee ${usd(flat)} plus ${pct(bps)} of ${usd(above)} above the split point ${usd(walkoutSplit)} (${usd(extra)}) = ${usd(dealAmount)}`
      }
    } else {
      const split = terms.splitPointCents ?? 0
      const above = Math.max(0, basisCents - split)
      const bonus = percentOf(above, bps)
      dealAmount = flat + bonus
      dealWork = `flat ${usd(flat)} plus ${pct(bps)} of ${usd(above)} above the split point ${usd(split)} (${usd(bonus)}) = ${usd(dealAmount)}`
    }
  }
  if (dealAmount !== null && terms.capCents !== null && terms.formula !== 'flat' && dealAmount > terms.capCents) {
    dealWork += `, capped at ${usd(terms.capCents)}`
    dealAmount = terms.capCents
  }
  if (dealAmount !== null && dealAmount < 0) {
    dealWork += '; a deal amount below zero counts as zero'
    dealAmount = 0
  }

  // The walkout point: the door at which the percentage starts paying more
  // than the guarantee (versus), or above the split point (plus, walkout).
  let walkoutDoorCents: number | null = null
  if (terms.kind === 'talent' && (terms.formula === 'versus' || terms.formula === 'plus' || walkoutFormula) && bps > 0 && door !== null && perTicketTotal !== null && supportCost !== null) {
    const target = terms.formula === 'versus' ? smallestReaching(flat, bps) : walkoutFormula ? walkoutSplit : (terms.splitPointCents ?? 0)
    if (target !== null) {
      const off = expensesInBasis ? beforeSplitTotal + supportCost : 0
      const allocatedNeeded = target + off
      const pre = share === null ? allocatedNeeded : smallestReaching(allocatedNeeded, share)
      if (pre !== null) {
        const ticketFees = terms.basis === 'gross' ? 0 : perTicketTotal
        walkoutDoorCents = Math.max(0, pre + ticketFees)
        const how: string[] = []
        if (terms.formula === 'versus') how.push(`${pct(bps)} of a basis of ${usd(target)} reaches the ${usd(flat)} guarantee`)
        else how.push(`the basis reaches the split point ${usd(target)}`)
        if (off) how.push(`add back show expenses ${usd(beforeSplitTotal)}${usesSupport ? ` and the other acts’ pay ${usd(supportCost)}` : ''}`)
        if (share !== null) how.push(`divide by the ${pct(share)} of the door allocated to this act`)
        if (ticketFees) how.push(`add back per-ticket fees ${usd(ticketFees)} on tonight’s paid tickets`)
        const diff = door - walkoutDoorCents
        const verdict =
          diff >= 0
            ? `Tonight’s door ${usd(door)} passed it by ${usd(diff)}${terms.formula === 'versus' ? ', so the percentage pays' : ', so the percentage above it pays'}.`
            : `Tonight’s door ${usd(door)} fell ${usd(-diff)} short of it${terms.formula === 'versus' ? ', so the guarantee pays' : ', so only the guarantee pays'}.`
        info('walkout', 'Walkout point (door gross)', `${how.join('; ')}. ${verdict}`, walkoutDoorCents)
      }
    }
  }

  let payout: number | null = null
  if (terms.kind === 'talent') {
    due('deal', terms.formula === 'flat' ? 'Guarantee' : 'Artist or promoter share', dealWork || 'needs the basis', dealAmount)
    // Bonuses (ADR 0040), added to the artist's share.
    let bonusTotal: number | null = 0
    const bonuses = terms.bonuses ?? []
    if (bonuses.length) {
      const tickets = bonuses.some((b) => b.measure === 'paid_tickets') ? need('paidTickets') : null
      const measured = bonuses.map((b) => {
        const value = b.measure === 'paid_tickets' ? tickets : door
        const name = b.label ?? (b.measure === 'paid_tickets' ? `${b.threshold.toLocaleString('en-US')} paid tickets` : `door of ${usd(b.threshold)}`)
        return { b, value, name, reached: value !== null && value >= b.threshold }
      })
      const highest = terms.bonusMode === 'highest'
      const best = highest ? measured.filter((m) => m.reached).sort((x, y) => y.b.amountCents - x.b.amountCents)[0] : undefined
      for (const [i, m] of measured.entries()) {
        const what = (v: number) => (m.b.measure === 'paid_tickets' ? `${v.toLocaleString('en-US')} paid tickets` : `door ${usd(v)}`)
        const at = m.b.measure === 'paid_tickets' ? m.b.threshold.toLocaleString('en-US') : usd(m.b.threshold)
        const key = `bonus_${i + 1}`
        const label = `Bonus: ${m.name}`
        if (m.value === null) {
          due(key, label, `${usd(m.b.amountCents)} at ${at}; the measure is missing`, null)
          bonusTotal = null
        } else if (!m.reached) {
          const short = m.b.threshold - m.value
          info(key, label, `${usd(m.b.amountCents)} at ${at}: not reached, ${what(m.value)} (${m.b.measure === 'paid_tickets' ? short.toLocaleString('en-US') : usd(short)} short)`, 0)
        } else if (highest && m !== best) {
          info(key, label, `${usd(m.b.amountCents)} at ${at}: reached with ${what(m.value)}, but only the largest bonus reached pays`, 0)
        } else {
          due(key, label, `${usd(m.b.amountCents)} at ${at}: reached with ${what(m.value)}`, m.b.amountCents)
          if (bonusTotal !== null) bonusTotal += m.b.amountCents
        }
      }
    }
    payout = dealAmount === null || bonusTotal === null ? null : dealAmount + bonusTotal
    if (terms.doorCollectedBy === 'counterparty') due('door_held', 'Door collected by the counterparty, owed to BRE', `the door ${door === null ? 'is missing' : usd(door)} belongs to BRE`, door === null ? null : -door)
  } else if (terms.kind === 'rental') {
    if (terms.doorCollectedBy === 'bre') due('door', 'Door collected by BRE for the renter', `ticket sales after discounts and refunds, before buyer fees`, door)
    if (terms.perTicketFeeCents) {
      const count = perTicketTotal === null ? null : bases.paidTickets.value
      due('per_ticket', terms.waterfall ? 'Facility fee per ticket kept by BRE' : 'Per-ticket fee kept by BRE', `${count === null ? 'paid tickets missing' : `${count} paid tickets`} × ${usd(terms.perTicketFeeCents)}`, perTicketTotal === null ? null : -perTicketTotal)
    }
    if (rw) {
      if (rw.facility !== 0 || terms.waterfall!.facilityFeesCents === null) due('facility_fees', 'Facility fees kept by BRE', rw.facility === null ? 'the facility fees inside the door are not known yet' : 'facility fees inside the door (per-tier facility fees)', rw.facility === null ? null : -rw.facility)
      if (rw.ticketing) due('ticketing_fees', 'Ticketing fees', 'taken from the door', -rw.ticketing)
      if (terms.waterfall!.ascapBps) due('ascap', 'ASCAP / BMI', rw.afterFees === null ? `${pct(terms.waterfall!.ascapBps)} of the door after fees (missing)` : `${pct(terms.waterfall!.ascapBps)} of the door after fees ${usd(rw.afterFees)}`, rw.ascap === null ? null : -rw.ascap)
      if (terms.waterfall!.cardFeeBps) due('card_fees', 'Card fees', rw.afterFees === null ? `${pct(terms.waterfall!.cardFeeBps)} of the door after fees (missing)` : `${pct(terms.waterfall!.cardFeeBps)} of the door after fees ${usd(rw.afterFees)}`, rw.card === null ? null : -rw.card)
    }
    due('rent', 'Rent', dealWork || 'needs the basis', dealAmount === null ? null : -dealAmount)
    payout = dealAmount
  } else {
    // A co-promote (ADR 0040): the show's net, after show expenses and the
    // acts' pay, split by the agreed percentages, profit or loss.
    const unknown = ctx.talent.filter((a) => a.payoutCents === null)
    if (unknown.length && !missing.some((m) => m.base === 'acts')) missing.push({ base: 'acts', label: 'The acts’ pay', reason: `The pay of ${unknown.map((a) => a.name).join(', ')} can’t be worked out yet, and it comes off the net.` })
    const talentCost = unknown.length ? null : ctx.talent.reduce((a, x) => a + x.payoutCents!, 0)
    const pre = door === null || perTicketTotal === null ? null : terms.basis === 'gross' ? door : door - perTicketTotal
    const expensesAll = total(allBefore)
    const net = pre === null || talentCost === null ? null : pre - expensesAll - talentCost
    const parts = [`door ${door === null ? 'missing' : usd(door)}`]
    if (terms.basis !== 'gross') parts.push(`less per-ticket fees ${perTicketTotal === null ? 'missing' : usd(perTicketTotal)}`)
    parts.push(`less show expenses ${usd(expensesAll)} (${plural(allBefore.length, 'line')})`)
    if (ctx.talent.length === 0) parts.push('less talent $0.00 (no act has a deal in this settlement; mark a talent cost as a show expense to count it)')
    else parts.push(`less the acts’ pay ${talentCost === null ? 'missing' : usd(talentCost)} (${ctx.talent.map((a) => `${a.name} ${a.payoutCents === null ? 'missing' : usd(a.payoutCents)}`).join(', ')})`)
    info('pool', 'Show net to split', parts.join(', '), net)
    basisCents = net
    const lossBps = terms.lossShareBps ?? bps
    if (net === null) due('copro_share', 'Their share of the net', 'needs the show net', null)
    else if (net >= 0) {
      dealAmount = percentOf(net, bps)
      due('copro_share', 'Their share of the profit', `${pct(bps)} of ${usd(net)} = ${usd(dealAmount)}`, dealAmount)
    } else {
      dealAmount = percentOf(net, lossBps)
      due('copro_share', 'Their share of the loss', `${pct(lossBps)} of the ${usd(-net)} loss = ${usd(-dealAmount)} they bear`, dealAmount)
    }
    payout = net === null ? null : dealAmount
    if (ctx.talent.length && allBefore.some((e) => e.category === 'talent'))
      warnings.push('A talent cost is marked as a show expense, and the acts’ deals pay them too: talent may be counted twice in the net.')
    if (terms.doorCollectedBy === 'counterparty') due('door_held', 'Door collected by the counterparty, owed to BRE', `the door ${door === null ? 'is missing' : usd(door)} belongs to BRE`, door === null ? null : -door)
  }
  if (terms.kind === 'talent' && terms.perTicketFeeCents && terms.basis === 'gross') warnings.push('A per-ticket fee is set, but the percentage is of the door gross, so the fee changes nothing.')

  // Fees BTownTix holds for someone else go to the lead counterparty when BRE collected the door.
  if (terms.doorCollectedBy === 'bre' && ctx.isLead) {
    const held = bases.feesForOthers
    if (held.missingReason !== null) {
      if (!missing.some((m) => m.base === 'feesForOthers')) missing.push({ base: 'feesForOthers', label: held.label, reason: held.missingReason })
      due('fees_for_others', 'Buyer fees held for the counterparty', 'who keeps each fee is unknown', null)
    } else if (held.value !== 0) due('fees_for_others', 'Buyer fees held for the counterparty', held.explanation, held.value)
  }

  if (terms.barSplitBps) {
    const bar = need('bar')
    due('bar', 'Bar share', bar === null ? `${pct(terms.barSplitBps)} of bar sales (missing)` : `${pct(terms.barSplitBps)} of ${usd(bar)} bar and food sales`, bar === null ? null : percentOf(bar, terms.barSplitBps))
  }

  if (terms.merchSoldBy !== 'none') {
    const merch = need('merch')
    const rate = terms.merchCommissionBps ?? 0
    const commission = merch === null ? null : percentOf(merch, rate)
    if (terms.merchSoldBy === 'venue') {
      due('merch_gross', 'Merch sold by BRE for the counterparty', merch === null ? 'merch sales missing' : `merch sales ${usd(merch)}`, merch)
      due('merch_commission', 'Merch commission kept by BRE', merch === null ? `${pct(rate)} of merch sales (missing)` : `${pct(rate)} of ${usd(merch)}`, commission === null ? null : -commission)
    } else {
      due('merch_commission', 'Merch commission owed to BRE', merch === null ? `${pct(rate)} of reported merch sales (missing)` : `${pct(rate)} of ${usd(merch)} the counterparty reported`, commission === null ? null : -commission)
    }
  }

  // Card processing charged to them (ADR 0040), its own step.
  if (terms.processing) {
    const p = terms.processing
    const tix = need('processing')
    const otherLines = lines.filter((l) => PROCESSING_CATEGORIES.includes(l.category) && l.source?.sourceService !== 'bre-ticketing')
    const others = p.include === 'all' ? otherLines.reduce((a, l) => a + l.amountCents, 0) : 0
    const cost = tix === null ? null : tix + others
    const what = p.include === 'all' && otherLines.length ? `BTownTix’s ${tix === null ? 'missing' : usd(tix)} plus ${usd(others)} other card processing (${plural(otherLines.length, 'line')})` : `BTownTix’s Stripe processing ${tix === null ? 'missing' : usd(tix)}`
    let charge: number | null = null
    let why = ''
    if (p.share === 'percent') {
      const rate = p.percentBps ?? 10000
      charge = cost === null ? null : percentOf(cost, rate)
      why = `${pct(rate)} of ${what}`
    } else if (terms.kind === 'co_promote') {
      const rate = basisCents !== null && basisCents < 0 ? (terms.lossShareBps ?? bps) : bps
      charge = cost === null ? null : percentOf(cost, rate)
      why = `${what} × their ${pct(rate)} share of the net`
    } else {
      // Their share of the door: their pay over the door gross, at most all of it.
      if (cost !== null && payout !== null && door !== null) {
        const theirs = Math.min(Math.max(payout, 0), Math.max(door, 0))
        charge = door <= 0 ? 0 : ratioOf(cost, theirs, door)
        why = door <= 0 ? `${what}; no door, so no share of it` : `${what} × their share of the door: ${usd(theirs)} of ${usd(door)} (${((theirs / door) * 100).toFixed(1)}%)`
      } else why = `${what} × their share of the door (needs their pay and the door)`
      if (door === null) need('door')
    }
    due('processing', 'Card processing charged', why, charge === null ? null : -charge)
    if (charged.some((e) => e.category === 'processing')) warnings.push('A card processing line is also marked “charged to the counterparty”: it is charged twice. Set that line back to BRE’s cost, or turn off the deal’s processing charge.')
  }

  for (const [i, e] of charged.entries()) due(`charged_${i + 1}`, `Expense charged: ${e.label}`, 'billed to the counterparty in full', -e.amountCents)
  for (const [i, a] of terms.advances.entries()) {
    const when = a.paidOn ? ` on ${a.paidOn}` : ''
    if (a.direction === 'to_counterparty') due(`advance_${i + 1}`, `Already paid to them: ${a.label}`, `BRE paid ${usd(a.amountCents)}${when}`, -a.amountCents)
    else due(`advance_${i + 1}`, `Already paid by them: ${a.label}`, `they paid ${usd(a.amountCents)}${when}`, a.amountCents)
  }
  for (const [i, a] of terms.adjustments.entries()) due(`adjustment_${i + 1}`, `Adjustment: ${a.label}`, a.note ?? 'entered by staff', a.amountCents)

  const complete = missing.length === 0 && steps.every((s) => s.role === 'info' || s.amountCents !== null)
  const amountDueCents = complete ? steps.filter((s) => s.role === 'due').reduce((acc, s) => acc + s.amountCents!, 0) : null
  return { complete, missing, warnings, bases, basisCents, dealAmountCents: dealAmount, steps, amountDueCents, roundingRule: ROUNDING_RULE_TEXT, payoutCents: payout, walkoutDoorCents }
}

export type PartyStatement = SettlementParty & { statement: DealStatement }

// Every party's statement (ADR 0040). The acts are worked out lowest-billed
// first, so an act whose split takes the acts below it as a show expense
// knows their pay; a co-promote comes last, after every act's pay.
export function computeSettlementDeals(parties: SettlementParty[], lines: LineLike[], sources: SettlementSourcePull[]): PartyStatement[] {
  const sorted = [...parties].sort((a, b) => a.position - b.position || a.key.localeCompare(b.key))
  if (sorted.length === 0) return []
  const keys = new Set(sorted.map((p) => p.key))
  const known = lines.map((l) => (l.dealPartyKey && !keys.has(l.dealPartyKey) ? { ...l, dealPartyKey: null } : l))
  const lead = sorted[0]!.key
  const out = new Map<string, DealStatement>()
  const talent = sorted.filter((p) => p.terms.kind === 'talent')
  for (let i = talent.length - 1; i >= 0; i--) {
    const p = talent[i]!
    const lower = talent.slice(i + 1).map((q) => ({ name: q.terms.counterpartyName, payoutCents: out.get(q.key)!.payoutCents ?? null }))
    out.set(p.key, computeDealStatement(p.terms, known, sources, { partyKey: p.key, isLead: p.key === lead, lower, talent: [], partyCount: sorted.length }))
  }
  const pay = talent.map((q) => ({ name: q.terms.counterpartyName, payoutCents: out.get(q.key)!.payoutCents ?? null }))
  for (const p of sorted) if (!out.has(p.key)) out.set(p.key, computeDealStatement(p.terms, known, sources, { partyKey: p.key, isLead: p.key === lead, lower: [], talent: pay, partyCount: sorted.length }))
  return sorted.map((p) => ({ ...p, statement: out.get(p.key)! }))
}

// ------------------------------------------------ the P&L after the deal

// The night's P&L with the deal in it (ADR 0040, the ADR 0039 follow-up):
// each act's pay as the deal works it out, not Booking's contracted
// guarantee, and every other step that moves BRE's money (a bar share, merch
// paid over, rent, a co-promoter's share, expenses recovered). Booking's
// contract figure stays, as a memo line. Advances, buyer fees held for
// others and a door the counterparty collected are not P&L: they say who
// holds the money, not what the night made.
export type DealPnlLine = { partyKey: string; counterpartyName: string; category: SettlementCategory; label: string; amountCents: number; explanation: string }
export type DealPnl = {
  complete: boolean
  missing: string[]
  // The deal's own lines.
  lines: DealPnlLine[]
  // App figures the deal replaces, kept as memo lines.
  replaced: { label: string; amountCents: number; why: string }[]
  // The P&L with the deal in it; null while a statement is incomplete.
  totals: SettlementTotals | null
}

export function settlementPnlAfterDeals(lines: LineLike[], parties: PartyStatement[]): DealPnl | null {
  if (parties.length === 0) return null
  const hasTalent = parties.some((p) => p.terms.kind === 'talent')
  const hasRental = parties.some((p) => p.terms.kind === 'rental')
  const replaced: DealPnl['replaced'] = []
  const base = lines.map((l) => {
    const key = l.source?.sourceService === 'booking' ? l.source.figureKey : undefined
    if (hasTalent && key === 'artist_guarantee') {
      replaced.push({ label: `${l.label} (contract figure)`, amountCents: l.amountCents, why: 'Memo: the deal’s computed pay for each act replaces it.' })
      return { category: 'memo', amountCents: l.amountCents }
    }
    if (hasRental && key === 'rental_charged') {
      replaced.push({ label: `${l.label} (agreement price)`, amountCents: l.amountCents, why: 'Memo: the deal’s rent replaces it.' })
      return { category: 'memo', amountCents: l.amountCents }
    }
    return { category: l.category, amountCents: l.amountCents }
  })
  const out: DealPnlLine[] = []
  const missing: string[] = []
  for (const p of parties) {
    const name = p.terms.counterpartyName
    if (!p.statement.complete) missing.push(`${name}: ${p.statement.missing.map((m) => m.label.toLowerCase()).join(', ') || 'incomplete'}`)
    for (const st of p.statement.steps) {
      if (st.role !== 'due' || st.amountCents === null) continue
      const a = st.amountCents
      const add = (category: SettlementCategory, amountCents: number, label: string) => out.push({ partyKey: p.key, counterpartyName: name, category, label, amountCents, explanation: st.explanation })
      if (st.key === 'deal') add('talent', a, `Talent per the deal: ${name}`)
      else if (st.key.startsWith('bonus_')) add('talent', a, `${st.label}: ${name}`)
      else if (st.key === 'copro_share') add('revenue_share', a, `${name}’s ${a >= 0 ? 'share of the profit' : 'share of the loss (they bear it)'}`)
      else if (st.key === 'door') add('revenue_share', a, `Door paid over to ${name}`)
      else if (st.key === 'per_ticket') add('ticket_fees', -a, `Per-ticket fee kept from ${name}’s door`)
      else if (st.key === 'facility_fees') add('ticket_fees', -a, `Facility fees kept from ${name}’s door`)
      else if (st.key === 'ticketing_fees') add('ticket_fees', -a, `Ticketing fees kept from ${name}’s door`)
      else if (st.key === 'ascap') add('other_cost', a, `ASCAP / BMI recovered from ${name}’s door`)
      else if (st.key === 'card_fees') add('processing', a, `Card fees recovered from ${name}’s door`)
      else if (st.key === 'rent') add('rental', -a, `Rent from ${name}`)
      else if (st.key === 'bar') add('revenue_share', a, `Bar share to ${name}`)
      else if (st.key === 'merch_gross') add('revenue_share', a, `Merch paid over to ${name}`)
      else if (st.key === 'merch_commission') {
        if (p.terms.merchSoldBy === 'venue') add('revenue_share', a, `Merch commission kept from ${name}’s merch`)
        else add('other_revenue', -a, `Merch commission from ${name}`)
      } else if (st.key === 'processing') add('processing', a, `Card processing charged to ${name}`)
      else if (st.key.startsWith('charged_')) add('other_cost', a, `Recovered from ${name}: ${st.label.replace(/^Expense charged: /, '')}`)
      else if (st.key.startsWith('adjustment_')) add('other_cost', a, `${st.label} (${name})`)
      // door_held, fees_for_others and advance_* are who holds the money, not P&L.
    }
  }
  const complete = missing.length === 0
  return { complete, missing, lines: out, replaced, totals: complete ? settlementTotals([...base, ...out]) : null }
}

// The deal in one sentence, for the statement's header.
export function describeDeal(t: SettlementDealTerms): string {
  const basis = DEAL_BASIS_LABELS[t.basis].toLowerCase()
  const money = (c: number | null) => (c === null ? '(not set)' : usd(c))
  const p = t.percentBps === null ? '(not set)' : pct(t.percentBps)
  const what = t.kind === 'talent' ? 'paid to the counterparty' : 'rent to BRE'
  let core: string
  if (t.kind === 'co_promote') {
    core = `${p} of the show’s net (door${t.basis === 'gross' ? '' : ' less per-ticket fees'}, less show expenses and the acts’ pay) to the co-promoter`
    if (t.lossShareBps != null && t.lossShareBps !== t.percentBps) core += `; they bear ${pct(t.lossShareBps)} of a loss`
    else core += ', profit or loss'
  } else if (t.formula === 'flat') core = `${money(t.flatCents)} flat, ${what}`
  else if (t.formula === 'percentage') core = `${p} of ${basis}, ${what}`
  else if (t.formula === 'versus') core = `the greater of ${money(t.flatCents)} or ${p} of ${basis}, ${what}`
  else if (t.formula === 'walkout') core = `${money(t.flatCents)} guarantee plus ${p} of ${t.basis === 'gross' ? 'door gross' : 'door net of per-ticket fees'} above the walkout (guarantee + show expenses + ${t.promoterProfitBps == null ? '0%' : pct(t.promoterProfitBps)} BRE profit), ${what}`
  else core = `${money(t.flatCents)} plus ${p} of ${basis} above ${money(t.splitPointCents)}, ${what}`
  if (t.capCents !== null && t.formula !== 'flat' && t.kind !== 'co_promote') core += `, capped at ${money(t.capCents)}`
  const extras: string[] = []
  if (t.doorShareBps != null) extras.push(`${pct(t.doorShareBps)} of the door is this act’s`)
  if (t.expenseShareBps != null) extras.push(`${pct(t.expenseShareBps)} of the shared show expenses come off it`)
  if (t.supportAsExpense) extras.push('the acts billed below are a show expense')
  if (t.bonuses?.length) {
    const words = t.bonuses.map((b) => `${usd(b.amountCents)} at ${b.measure === 'paid_tickets' ? `${b.threshold.toLocaleString('en-US')} paid tickets` : `a ${usd(b.threshold)} door`}`)
    extras.push(`bonus${t.bonuses.length === 1 ? '' : 'es'} ${words.join(', ')}${t.bonuses.length > 1 ? (t.bonusMode === 'highest' ? ' (the largest reached pays)' : ' (each reached pays)') : ''}`)
  }
  if (t.perTicketFeeCents) extras.push(`${usd(t.perTicketFeeCents)} per paid ticket to BRE`)
  if (t.barSplitBps) extras.push(`${pct(t.barSplitBps)} of bar sales to the counterparty`)
  if (t.merchSoldBy !== 'none') extras.push(`${t.merchCommissionBps === null ? '(not set)' : pct(t.merchCommissionBps)} merch commission to BRE (${t.merchSoldBy === 'venue' ? 'BRE sells' : 'they sell'})`)
  if (t.waterfall) {
    const w = t.waterfall
    const bits = [w.facilityFeesCents === null ? 'facility fees (to be entered)' : w.facilityFeesCents ? `facility fees ${usd(w.facilityFeesCents)}` : null, w.ticketingFeesCents ? `ticketing fees ${usd(w.ticketingFeesCents)}` : null, w.ascapBps ? `${pct(w.ascapBps)} ASCAP/BMI` : null, w.cardFeeBps ? `${pct(w.cardFeeBps)} card fees` : null].filter(Boolean)
    extras.push(`Facility-fee waterfall${bits.length ? `: ${bits.join(', ')} off the door` : ''}; expenses charged to the renter come off before BRE’s share`)
  }
  if (t.processing) extras.push(`card processing charged to them (${t.processing.share === 'percent' ? pct(t.processing.percentBps ?? 10000) : 'their share'}${t.processing.include === 'all' ? ', all processing' : ', BTownTix’s'})`)
  const more = extras.join('; ')
  return `${core[0]!.toUpperCase()}${core.slice(1)}.${more ? ` ${more[0]!.toUpperCase()}${more.slice(1)}.` : ''} Door collected by ${t.doorCollectedBy === 'bre' ? 'BRE' : 'the counterparty'}.`
}

// ------------------------------------------------- figures changed since

export type FigureChange = {
  sourceService: SettlementSourcePull['sourceService']
  sourceId: string
  title: string
  key: string
  label: string
  // null: the figure wasn't there.
  frozenCents: number | null
  currentCents: number | null
}

export type SourceComparison = {
  // Figures that differ between this version's frozen pull and what the app says now.
  changed: FigureChange[]
  // Apps that can't be compared (not answering now, or not then), in words.
  uncomparable: string[]
  // A linked record that wasn't in the frozen pull, or is no longer linked.
  linksChanged: string[]
}

// What changed in the apps since a version froze its figures. A signed
// settlement never changes by itself: this only says "figures changed
// since this version", and a person decides whether to revise.
export function compareSourcePulls(frozen: SettlementSourcePull[], current: SettlementSourcePull[]): SourceComparison {
  const keyOf = (p: SettlementSourcePull) => `${p.sourceService}|${p.entityType}|${p.sourceId}`
  const now = new Map(current.map((p) => [keyOf(p), p]))
  const then = new Map(frozen.map((p) => [keyOf(p), p]))
  const out: SourceComparison = { changed: [], uncomparable: [], linksChanged: [] }
  for (const [k, f] of then) {
    const c = now.get(k)
    if (!c) {
      out.linksChanged.push(`${f.title} is no longer linked to the event.`)
      continue
    }
    if (f.status !== 'ok' || c.status !== 'ok') {
      out.uncomparable.push(f.status !== 'ok' ? `${f.title} didn’t answer when this version was pulled.` : `${c.title} isn’t answering now (${c.reason ?? c.status}).`)
      continue
    }
    const keys = new Set([...f.figures.map((x) => x.key), ...c.figures.map((x) => x.key)])
    for (const key of keys) {
      const a = f.figures.find((x) => x.key === key)
      const b = c.figures.find((x) => x.key === key)
      if ((a?.amountCents ?? null) !== (b?.amountCents ?? null)) {
        out.changed.push({ sourceService: f.sourceService, sourceId: f.sourceId, title: f.title, key, label: (b ?? a)!.label, frozenCents: a?.amountCents ?? null, currentCents: b?.amountCents ?? null })
      }
    }
  }
  for (const [k, c] of now) if (!then.has(k)) out.linksChanged.push(`${c.title} was linked after this version was pulled.`)
  return out
}

// Hub's own expense quick picks, beside Booking's catalog (provisional,
// ADR 0039: the owner hasn't settled the list).
export const HUB_EXPENSE_PICKS: { ref: string; label: string; category: SettlementCategory }[] = [
  { ref: 'stagehands', label: 'Stagehands and production labor', category: 'production' },
  { ref: 'sound_light', label: 'Sound and lights', category: 'production' },
  { ref: 'police', label: 'Off-duty police', category: 'security' },
  { ref: 'security', label: 'Event security', category: 'security' },
  { ref: 'ushers', label: 'Ushers and ticket takers', category: 'staffing' },
  { ref: 'cleaning', label: 'Cleaning', category: 'staffing' },
  { ref: 'hospitality', label: 'Hospitality and catering rider', category: 'supplies' },
  { ref: 'advertising', label: 'Advertising', category: 'marketing' },
  { ref: 'ascap_bmi', label: 'ASCAP / BMI', category: 'other_cost' },
  { ref: 'insurance', label: 'Insurance', category: 'other_cost' },
  { ref: 'equipment', label: 'Equipment rental', category: 'production' },
]
